import { LockOutlined, Share } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import classSet from 'react-classset';
import { Link } from 'react-router-dom';

import alertIcon from '@/assets/icons/vn-icon-alert.svg';
import { useModalContext } from '@/modals/VNModalProvider';
import { accessUtils } from '@/utils/accessUtils';
import itemUtils from '@/utils/itemUtils';

import { Permission } from '../../enums';
import type { Item } from '../../models';
import type { ChannelSubtypes } from '../../models/item';
import type { Permissions } from '../../models/Permissions';
import VNChip from '../VNChip/VNChip';

const SUBTYPE_CHIP_LABELS: Partial<Record<ChannelSubtypes, string>> = {
  Series: 'Series',
  Season: 'Season',
  Extra: 'Extra',
  Trailer: 'Trailer',
};

export interface Iprops {
  item: Item;
  permissions?: Permissions;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;
  href?: string;
  hideStatus?: boolean;
}

const FuelAssetThumbnail = ({ item, permissions, onClick, hideStatus = false, href }: Iprops) => {
  const modal = useModalContext();
  const Component = useMemo(() => (href ? Link : 'div'), [href]);

  const hasPermission = useMemo(() => !permissions || permissions.has(Permission.EditAsset), [permissions]);

  const showPrivateIcon = useMemo(() => accessUtils.isEffectiveAccessPrivate(item), [item]);
  const showShareIcon = useMemo(() => accessUtils.shouldShowContentSharingIcon(item), [item]);
  const classes = useMemo(
    () =>
      classSet({
        'fuel-asset-thumbnail': true,
        'collection-thumbnail': ['ClipGroup', 'NestedChannel'].includes(item?.source),
        'nested-channel-clip': item?.source === 'NestedChannel',
        'live-event-clip': item?.source === 'LiveEvent',
        'no-clickable': !hasPermission, // blocks click on warnings too
        'no-pointer': !onClick && !href,
        [`thumbnail-border-${item?.clipStatusMarker}`]: !hideStatus && item?.clipStatusMarker !== 'none',
      }),
    [item?.source, item?.clipStatusMarker, hideStatus, hasPermission, onClick, href]
  );

  const onClickIngestWarnings = (e: MouseEvent<HTMLButtonElement>, asset: Item) => {
    e.stopPropagation();

    switch (asset?.ingestWarnings[0]) {
      case 'no-audio':
        modal.onOpen(
          'Missing Audio Track',
          'xl',
          <div className="fuel-asset-thumbnail-warning-modal">
            <div className="warning-modal-inner">
              <img src={alertIcon} alt="warning-alert" className="warning-modal-inner__icon" />
              <p>
                This video file does not have an audio track. It will not playout in the stream it is associated with if
                published. Please remove it and upload a corrected version. (Note: the file does not have to have audio, but it
                needs at least one audio track)
              </p>
            </div>
          </div>,
          asset.id
        );
        break;
      default:
    }
  };

  const setDefaultImg = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = '/static/images/thumbnail.png';
  };

  const subtypeChipLabel = item?.data?.subtype ? SUBTYPE_CHIP_LABELS[item.data.subtype] : undefined;
  const clipIcon = !subtypeChipLabel && item ? itemUtils.getClipIcon(item) : null;

  return (
    <Component
      role="link"
      tabIndex={0}
      className={classes}
      title={item?.differenceVariantCodesWithChannel ? `Missing variants: ${item?.differenceVariantCodesWithChannel}` : ''}
      onClick={(event) => onClick?.(event)}
      to={href}
    >
      <div className="thumbnail-container">
        {item?.ingestWarnings?.length > 0 && (
          <Tooltip title="click to view details" placement="top">
            <IconButton className="ingest-warning" onClick={(e) => onClickIngestWarnings(e, item)}>
              {/* <FontAwesomeIcon icon={faTriangleExclamation} /> */}
              <img src={alertIcon} alt="warning-alert" />
            </IconButton>
          </Tooltip>
        )}

        <img
          className="group-thumbnail"
          src={item?.thumbnailSrc || '/static/images/thumbnail.png'}
          loading="lazy"
          alt=""
          onError={setDefaultImg}
        />
        <img
          className="thumbnail"
          src={item?.thumbnailSrc || '/static/images/thumbnail.png'}
          loading="lazy"
          alt=""
          onError={setDefaultImg}
        />

        {subtypeChipLabel && <VNChip label={subtypeChipLabel} tone="neutral" variant="filled" className="subtype-chip" />}
        {clipIcon && <img className="clip-group-icon" src={clipIcon} alt="" />}

        {showPrivateIcon && (
          <Tooltip title="Private Access" placement="top">
            <span className="private-access-icon" aria-label="Private access">
              <LockOutlined fontSize="small" />
            </span>
          </Tooltip>
        )}

        {showShareIcon && (
          <Tooltip title="Shared With Parent Partner" placement="top">
            <span className="content-sharing-icon" aria-label="Shared content">
              <Share fontSize="small" />
            </span>
          </Tooltip>
        )}
      </div>
    </Component>
  );
};

export default FuelAssetThumbnail;
